import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { SiaLayoutApi } from '../apis/sia-layout.api';
import { DrawerPlacement } from '../interfaces/drawer-configuration';
import { SiaLayout } from '../interfaces/sia-layout';
import { SiaCacheDatabaseService } from './cache-database.service';
import { SiaSignalrService } from './signalr.service';

@Injectable({
  providedIn: 'root',
})
export class SiaLayoutService {
  #api = inject(SiaLayoutApi);
  #cacheDatabaseService = inject(SiaCacheDatabaseService);
  #signalrService = inject(SiaSignalrService);

  #inFlight = new Map<string, Promise<SiaLayout>>();
  #allLayoutsInFlight: Promise<SiaLayout[]> | undefined;

  drawerPlacement = signal<DrawerPlacement>('right');

  #panelLayoutShownSubject: Subject<string> = new Subject<string>();

  #allLayoutUpdatedSignal = signal<boolean>(false);
  #allLayoutUpdated$ = toObservable(this.#allLayoutUpdatedSignal);

  #additionalTitleSignal = signal<{ [guid: string]: string | undefined }>({});
  #additionalTitle$ = toObservable(this.#additionalTitleSignal);

  #departmentChangeOverlaySignal = signal<boolean>(false);
  #departmentChangeOverlay$ = toObservable(this.#departmentChangeOverlaySignal);

  #sidebarOpenSubject: Subject<boolean> = new Subject<boolean>();
  #toggleSidebarSubject: Subject<void> = new Subject<void>();

  get departmentChangeOverlay$() {
    return this.#departmentChangeOverlay$;
  }
  get allLayoutUpdated$() {
    return this.#allLayoutUpdated$;
  }
  get panelLayoutShown$() {
    return this.#panelLayoutShownSubject.asObservable();
  }

  get sidebarOpen$() {
    return this.#sidebarOpenSubject.asObservable();
  }
  get toggleSidebar$() {
    return this.#toggleSidebarSubject.asObservable();
  }

  constructor() {
    this.#signalrService.refreshCollectionsMessage$.subscribe((messageKeys: { [key: string]: boolean }) => {
      const messageKeysLower = Object.keys(messageKeys).map((key) => key.toLowerCase());
      if (messageKeys['*'] || messageKeysLower.some((key) => key.includes('layouts'))) {
        void (async () => {
          // Preload atomico (clear + bulk fetch + bulk write) invece di solo clear:
          // cosi quando i consumer reagiscono ad allLayoutUpdated$ trovano la cache calda
          // (evita un burst di N HTTP GET /api/layout/{id} concorrenti).
          try {
            await this.getAllLayouts();
          } catch {
            // se il bulk fetch fallisce, lascia comunque cache pulita per fallback lazy
            await this.#cacheDatabaseService.clearAllLayouts();
          }
          this.#allLayoutUpdatedSignal.set(true);
        })();
      }
    });
  }

  //#region public methods

  async updateLayout(l: SiaLayout) {
    const layout = await this.#api.updateLayout(l);
    if (layout) {
      await this.#cacheDatabaseService.updateLayout(layout);
      return this.updateLayouts();
    } else {
      return false;
    }
  }

  async updateLayouts(): Promise<any> {
    const done: any = await this.#cacheDatabaseService.clearAllLayouts();
    this.#allLayoutUpdatedSignal.set(done);
    return done;
  }

  // OK CACHE
  async getLayout(layoutId: string): Promise<SiaLayout> {
    layoutId = layoutId?.toLowerCase();
    const cached: SiaLayout = await this.#cacheDatabaseService.getLayout(layoutId);
    if (cached) {
      return cached;
    }
    const existing = this.#inFlight.get(layoutId);
    if (existing) {
      return existing;
    }
    const promise = (async () => {
      const layout = await this.#api.getLayout(layoutId);
      if (layout) {
        await this.#cacheDatabaseService.setLayout(layout);
      }
      return layout;
    })().finally(() => {
      this.#inFlight.delete(layoutId);
    });
    this.#inFlight.set(layoutId, promise);
    return promise;
  }
  getLayouts(): Promise<SiaLayout[]> {
    return this.#cacheDatabaseService.getLayouts();
  }

  async getAllLayouts(): Promise<SiaLayout[]> {
    // Dedup: se c'e' gia una bulk fetch in volo (es. listener SignalR e reloadUserMenus
    // innescati dallo stesso evento), riusala invece di duplicare la chiamata HTTP.
    if (this.#allLayoutsInFlight) {
      return this.#allLayoutsInFlight;
    }
    const promise = (async () => {
      const layouts = await this.#api.getLayouts();
      await this.#cacheDatabaseService.clearAllLayouts();
      await this.#cacheDatabaseService.setLayouts(layouts);
      return layouts;
    })().finally(() => {
      this.#allLayoutsInFlight = undefined;
    });
    this.#allLayoutsInFlight = promise;
    return promise;
  }

  showAdditionalTitle(title: string | undefined, guid: string) {
    const key = guid ?? '';
    if (!key) {
      return;
    }
    this.#additionalTitleSignal.update((current) => ({
      ...current,
      [key]: title,
    }));
  }

  additionalTitle$() {
    return this.#additionalTitle$;
  }

  getAdditionalTitle(guid: string): string | undefined {
    return this.#additionalTitleSignal()[guid];
  }

  showPanelLayout(panelId: string) {
    this.#panelLayoutShownSubject.next(panelId);
  }

  // Department change overlay methods
  showDepartmentChangeOverlay() {
    this.#departmentChangeOverlaySignal.set(true);
  }

  hideDepartmentChangeOverlay() {
    this.#departmentChangeOverlaySignal.set(false);
  }

  //#endregion

  setSidebarStatuts(value: boolean) {
    this.#sidebarOpenSubject.next(value);
  }
  toggleSidebar() {
    this.#toggleSidebarSubject.next();
  }
}
